/**
 * The few kinds of formatting a lab can use in a report's Comments: bold, italic, underline,
 * a heading, bullet and numbered lists. It is stored as plain text with light markers, so a
 * comment is just a string, and it is turned into data (never HTML) for the screen and the PDF.
 *
 *   **bold**  *italic*  __underline__   "## Heading"   "- bullet"   "1. numbered"
 *   A backslash makes the next marker an ordinary character: \*  \_  \\
 */

export interface Run {
  s: string;
  b?: true;
  i?: true;
  u?: true;
}
export type RichBlock =
  | { t: 'p'; runs: Run[] }
  | { t: 'h'; runs: Run[] }
  | { t: 'ul'; items: Run[][] }
  | { t: 'ol'; items: Run[][] };

/** How long Comments may be. */
export const COMMENTS_MAX = 5000;

export function parseInline(src: string): Run[] {
  const runs: Run[] = [];
  let b = false;
  let i = false;
  let u = false;
  let cur = '';
  const flush = () => {
    if (!cur) return;
    runs.push({
      s: cur,
      ...(b ? { b: true as const } : {}),
      ...(i ? { i: true as const } : {}),
      ...(u ? { u: true as const } : {}),
    });
    cur = '';
  };
  for (let k = 0; k < src.length; k++) {
    const c = src[k] as string;
    if (c === '\\' && k + 1 < src.length) {
      cur += src[k + 1];
      k++;
    } else if (c === '*' && src[k + 1] === '*') {
      flush();
      b = !b;
      k++;
    } else if (c === '_' && src[k + 1] === '_') {
      flush();
      u = !u;
      k++;
    } else if (c === '*') {
      flush();
      i = !i;
    } else {
      cur += c;
    }
  }
  flush();
  return runs;
}

export function parseRich(md: string): RichBlock[] {
  const blocks: RichBlock[] = [];
  let para: string[] = [];
  let list: { t: 'ul' | 'ol'; items: Run[][] } | null = null;
  const endPara = () => {
    if (para.length) blocks.push({ t: 'p', runs: parseInline(para.join('\n')) });
    para = [];
  };
  const endList = () => {
    if (list) blocks.push(list);
    list = null;
  };
  for (const raw of md.slice(0, COMMENTS_MAX * 2).split('\n')) {
    const line = raw.replace(/\s+$/, '');
    const head = /^#{1,3}\s+(.*)$/.exec(line);
    const ul = /^\s*[-*•]\s+(.*)$/.exec(line);
    const ol = /^\s*\d{1,3}[.)]\s+(.*)$/.exec(line);
    if (head) {
      endPara();
      endList();
      blocks.push({ t: 'h', runs: parseInline(head[1] ?? '') });
    } else if (ul || ol) {
      endPara();
      const kind = ul ? 'ul' : 'ol';
      if (!list || list.t !== kind) {
        endList();
        list = { t: kind, items: [] };
      }
      list.items.push(parseInline((ul ?? ol)?.[1] ?? ''));
    } else if (!line.trim()) {
      endPara();
      endList();
    } else {
      endList();
      para.push(line);
    }
  }
  endPara();
  endList();
  return blocks;
}

/** Plain words with the markers removed, for search and for a short summary. */
export function richToPlain(md: string): string {
  const text = (runs: Run[]) => runs.map((r) => r.s).join('');
  return parseRich(md)
    .map((b) => ('items' in b ? b.items.map(text).join('\n') : text(b.runs)))
    .join('\n');
}

/** Escape what would otherwise be read as a marker. */
export const escapeRich = (s: string): string => s.replace(/[\\*_]/g, (c) => `\\${c}`);
